import { Predicate } from '#/CoreFrameInterfaces';
import { toLocaleString } from '@/Utility/NumericTypeHelper';

export default class Utilities {

    public static isBlank(value: string): boolean {
        return (value as any) !== 0 && (!value || /^\s*$/.test(value));
    }

    public static isNotBlank(value: string): boolean {
        return !Utilities.isBlank(value);
    }

    public static isInt(value: any): boolean {
        return typeof value === 'number' &&
            isFinite(value) &&
            Math.floor(value) === value;
    }

    public static equalsIgnoreCase(string1: string, string2: string): boolean {
        if (string1 == string2) {
            return true;
        }

        if (null === string1 || undefined === string1) {
            return false;
        }

        if (null === string2 || undefined === string2) {
            return false;
        }

        return string1.toUpperCase() === string2.toUpperCase();
    }

    public static contains(haystack: string, needle: string): boolean {
        return haystack.indexOf(needle) !== -1;
    }

    public static startsWith(str: string, word: string): boolean {
        return !!str && !!word && !str.lastIndexOf(word, 0);
    }

    public static endsWith(str: string, word: string): boolean {
        let offset = word && str ? str.length - word.length : -1
        return offset >= 0 && str.lastIndexOf(word, offset) === offset
    }

    public static findFirst<T>(collection: Array<T>, test: Predicate): T {
        if (_.isEmpty(collection)) {
            return null;
        }

        for (let item of collection) {
            if (test(item)) {
                return item;
            }
        }

        return null;
    }

    /**
     * @deprecated Use `validateInputForSoftHyphens` instead.
     * 
     * @param value Eingabestring
     * @return true, wenn die Eingabe KEIN 'soft hyphen' Zeichen enthält, sonst false.
     */
    public static containsSoftHyphens(value: string): boolean {
        return this.validateInputForSoftHyphens(value);
    }

    /**
     * Validiert, ob eine Eingabe das 'soft hyphen' Zeichen enthält.
     * 
     * @param value Eingabestring
     * @returns true, wenn die Eingabe KEIN 'soft hyphen' Zeichen enthält, sonst false.
     */
    public static validateInputForSoftHyphens(value: string): boolean {
        if (!value)
            return true;

        if (!Utilities.isString(value))
            return true;

        const replaced = value.replace(/\u00AD/g, '');
        const isEqual = replaced === value;
        return isEqual;
    }

    public static swap(collection: Array<any>, firstIndex: number, secondIndex: number): void {
        if (_.isEmpty(collection) ||
            Utilities.isNullOrUndefined(firstIndex) ||
            Utilities.isNullOrUndefined(secondIndex)) {
            return;
        }

        if (firstIndex == secondIndex ||
            firstIndex < 0 || secondIndex < 0 ||
            firstIndex >= collection.length || secondIndex >= collection.length) {
            return;
        }

        var temp = collection[firstIndex];
        collection[firstIndex] = collection[secondIndex];
        collection[secondIndex] = temp;
    }

    public static isFunction(func): func is Function {
        return typeof func === 'function';
    }

    public static isNumeric(val): boolean {
        return !Array.isArray(val) && (val - parseFloat(val) + 1) >= 0;
    }

    public static isString(val): boolean {
        return typeof val === 'string' || (val as any) instanceof String
    }

    /** Prüft, ob die Taste eine Bestätigungstaste (Enter oder Leertaste) ist. */
    public static isConfirmKey(key: string): boolean {
        return key === 'Enter' || key === ' ' || key === 'Spacebar';
    }

    public static isNullOrUndefined(obj): boolean {
        return obj === null || obj === undefined;
    }

    public static isObservableOrValueNullOrUndefined<T>(obs: KnockoutObservable<T>): boolean {
        return Utilities.isNullOrUndefined(obs) || Utilities.isNullOrUndefined(obs());
    }

    public static isAnyNullOrUndefined(...objs: any[]): boolean {
        for (let obj of objs){
            if (Utilities.isNullOrUndefined(obj))
                return true;
        }
        
        return false;
    }

    public static isNullOrUndefinedOrBlank(obj): boolean {
        return Utilities.isNullOrUndefined(obj) || Utilities.isBlank(obj);
    }

    public static encodeXmlChars(str: string): string {
        return str.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }

    public static toLocaleNumber(number: number, floatingPrecision?: number): string {
        // Use this local require statement to prevent circle references (e.g. resourcemanager)
        //let NumericTypeHelperT = require('@/Utility/NumericTypeHelper');
        return toLocaleString(number, floatingPrecision);
    }

    public static wrap(before?, fn?, after?) {
        before && before();
        fn && fn();
        after && after();
    }

    public static isIE(): boolean {
        const ua = window.navigator.userAgent;
        return /MSIE|Trident/.test(ua);
    }

    public static isFirefox(): boolean {
        const ua = window.navigator.userAgent.toLowerCase();
        return ua.indexOf('firefox') > -1;
    }

    /**
     * Gibt ein direkt aufgelöstes Promise vom entsprechenden Typ zurück
     * @param resolveValue
     */
    public static resolveImmediately<T>(resolveValue?: T): JQueryPromise<T> {
        return $.Deferred().resolve(resolveValue).promise();
    }

    /**
     * Erstellt eine UUID
     */
    public static getUuid(): string {
        const s4 = () => {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };

        return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
    }

    /**
     * Prüft, ob ein String eine URL ist
     * @param data
     */
    public static isUrl(data: string): boolean {
        return data && (data.indexOf('http://') === 0 || data.indexOf('https://') === 0 || data.indexOf('file://') === 0);
    }

    /**
     * Fokussiert ein fokussierbares Element
     * Ist es nicht fokussierbar, so wird nach dem ersten fokussierbaren Kindelement gesucht
     * @param element
     * @returns Ob das Element anschließend als fokussiert gilt.
     */
    public static focusFirstFocusableElement(element: JQuery<HTMLElement>): boolean {
        let elementToFocus = element.is(':focusable')
            ? element
            : element.find(':focusable')
                .not('.c-link')
                .not('.k-tabstrip')
                .not('area');

        if (elementToFocus?.length > 0) {
            elementToFocus = elementToFocus.first();
        }

        elementToFocus?.focus();
        return elementToFocus?.is(':focus') ?? false;
    }

    /**
     * Gibt die Eingabefunktion "debounced" zurück
     * Die Eingabe wird verzögert bearbeitet. Sobald eine weitere Eingabe durchgeführt wurde, wird die bisherige Wartezeit der Verzögerung zurückgesetzt.
     * Ab der letzten Eingabe wird "wait" (ms) gewartet bis es weiterverarbeitet wird.
     * @param func
     * @param wait
     * @param immediate
     */
    public static debounce<T extends (...args: any[]) => any>(func: T, wait: number, immediate: boolean = false): Cancelable<T> {
        return _.debounce(func, wait, immediate);
    }

    /**
     * Gibt die Eingabefunktion "gethrottled" zurück
     * Sie wird in Abständen von "wait" (ms) ausgeführt
     * Der erste Aufruf wird standardmäßig erst nach "limit" ausgeführt
     * @param func
     * @param limit
     * @param options
     */
    public static throttle<T extends (...args: any[]) => any>(func: T, limit: number, options: _.ThrottleSettings = { leading: false }): Cancelable<T> {
        return _.throttle(func, limit, options);
    }

    /**
     * Returns true if the element is a child of the wrapper or is the same element as the wrapper
     * @param element
     * @param wrapper
     */
    public static isElementSameOrChildOf(element: JQuery | Element, wrapper: JQuery | Element): boolean {
        const plainElement = element instanceof jQuery ? (element as JQuery)[0] : element as Element;
        const plainWrapper = wrapper instanceof jQuery ? (wrapper as JQuery)[0] : wrapper as Element;

        if (Utilities.isNullOrUndefined(plainElement) || Utilities.isNullOrUndefined(plainWrapper))
            return false;

        return $.contains(plainWrapper, plainElement) || $(plainWrapper).is(element);
    }

    public static handlePdoId(pdoId: number | string) {
        if (Utilities.isNullOrUndefinedOrBlank(pdoId)) {
            return "-1";
        }

        return pdoId + '';
    }

    /**
     * Prüft eine Eingabe auf diakritische Zeichen
     * 
     * @param input Zu prüfende Eingabe
     * @returns true, wenn die Eingabe ein diakritisches Zeichen enthält, sonst false
     */
    public static containsDiacritics(input: string): boolean {
        //Quelle: https://stackoverflow.com/questions/61519041/regex-to-detect-zalgo
        if (!input)
            return false;
        /**
         * \u0300-\u036e Combining Diacritical Marks 
         * \u1dc0-\u1dfe Combining Diacritical Marks Supplement
         * \u20d0-\u20fe Combining Diacritical Marks for Symbols
         */
        if (input.match('[\u0300-\u036e\u1dc0-\u1dfe\u20d0-\u20fe]+')) {
            return true;
        }
        return false;
    }

    /**
     * Validiert eine Eingabe auf unültige Zeichen wie 'soft hyphen' oder Zalgo
     * 
     * @param value Zu prüfende Eingabe
     * @returns false, wenn die Eingabe ungültige Zeichen enthält, sonst true
     */
    public static validateInputForInvalidCharacters(value: string): boolean {
        if (!value)
            return true;

        if (!Utilities.isString(value))
            return true;

        return this.validateInputForSoftHyphens(value) && !this.containsDiacritics(value);
    }

    /**
     * Gibt an ob sich das Endgerät in einer mobilen bzw kleineren Ansicht befindet
     * @returns true, wenn eine mobile Ansicht vorliegt
     */
    public static isMobileView(): boolean {
        return Utilities.isMobileDevice();
    }

    private static isMobileDevice(): boolean {
        // https://stackoverflow.com/a/11381730 in combination with: http://detectmobilebrowsers.com/
        let check = false;
        ((agent) => {
            const regexPartOne = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i
            const regexPartTwo = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i
            if (regexPartOne.test(agent) || regexPartTwo.test(agent.substr(0, 4)))
                check = true;
        })(navigator.userAgent || navigator.vendor);

        return check;
    }
}